import React from 'react';
import { motion } from 'framer-motion';
import { Code, LineChart, CloudLightning, BarChart4, Bot, Lock } from 'lucide-react';

const features = [
  {
    title: 'No Coding Required',
    description: 'Create and manage trading bots without any programming knowledge through our intuitive interface.',
    icon: <Code className="w-12 h-12 text-blue-500" />
  },
  {
    title: 'TradingView Integration',
    description: 'Connect TradingView signals directly to your bots for advanced technical analysis-based trading.',
    icon: <LineChart className="w-12 h-12 text-purple-500" />
  },
  {
    title: 'Exchange APIs',
    description: 'Securely connect to popular crypto exchanges through API keys for automated trading.',
    icon: <CloudLightning className="w-12 h-12 text-pink-500" />
  },
  {
    title: 'Real-time Statistics',
    description: 'Monitor your trading performance with comprehensive real-time dashboards and analytics.',
    icon: <BarChart4 className="w-12 h-12 text-blue-500" />
  },
  {
    title: 'Custom Risk Profiles',
    description: 'Set your preferred risk level and let our system optimize trading parameters accordingly.',
    icon: <Bot className="w-12 h-12 text-purple-500" />
  },
  {
    title: 'Bank-grade Security',
    description: 'Your API keys are encrypted with enterprise-grade security. We never have access to your funds.',
    icon: <Lock className="w-12 h-12 text-pink-500" />
  }
];

export const Features: React.FC = () => {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5 }
    }
  };

  return (
    <section className="py-20 bg-white dark:bg-gray-900">
      <div className="container mx-auto px-4">
        <motion.div 
          className="text-center mb-16"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <h2 className="text-3xl md:text-4xl font-bold mb-4 text-gray-900 dark:text-white">
            Powerful <span className="bg-clip-text text-transparent bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500">Trading Features</span>
          </h2>
          <p className="text-lg text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
            BOTINO.NET offers everything you need to automate your cryptocurrency trading without writing a single line of code.
          </p>
        </motion.div>

        <motion.div 
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          {features.map((feature, index) => (
            <motion.div 
              key={index} 
              variants={itemVariants}
              className="bg-gray-50 dark:bg-gray-800 rounded-xl p-8 shadow-lg hover:shadow-xl transition-shadow border border-gray-100 dark:border-gray-700"
            >
              <div className="mb-4">{feature.icon}</div>
              <h3 className="text-xl font-bold mb-3 text-gray-900 dark:text-white">{feature.title}</h3>
              <p className="text-gray-600 dark:text-gray-400">{feature.description}</p>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};